import { invitationMeta } from './invitationMeta.js';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  ORGANIZATION_SCHEMA,
  SITE_NAME,
  SITE_URL,
} from './siteConfig.js';

const DEFAULT_ROBOTS = 'index, follow';
const NOINDEX_ROBOTS = 'noindex, nofollow';

function normalizePath(pathname = '/') {
  const trimmed = String(pathname || '/').trim();
  if (!trimmed || trimmed === '/') return '/';
  const withoutQuery = trimmed.split('?')[0].split('#')[0] || '/';
  const normalized = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
}

function toAbsoluteUrl(pathname = '/') {
  return new URL(normalizePath(pathname), `${SITE_URL}/`).toString();
}

function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: toAbsoluteUrl(item.path),
    })),
  };
}

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: 'en-PH',
};

const digitalInvitationFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is a digital invitation?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'A digital invitation is an online invitation page you can share through Messenger, Facebook, email, or chat links. It is easier to update than a static image and works well for mobile guests.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does a digital invitation cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'The standard E-Invitation package starts at P800 and includes the core invitation website features, RSVP flow, mobile-friendly layout, and shareable link. Optional add-ons are available for rush handling, extra photos, printable files, and other special requests.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is included in the standard package?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'The standard package includes a personalized digital invitation website, animated welcome screen, event details, countdown, background music, RSVP form, guest tracking, maps, seat and attendee tracker, and up to 10 photos.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I request a personalized digital invitation?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes. You can choose a sample design direction or request a personalized concept based on your event theme, colors, photos, and special details.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is RSVP included in the invitation flow?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes. The service is built around RSVP-ready invitation pages so guests can open the link and respond more easily than with a static file alone.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long does delivery usually take?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Standard delivery is 2-3 days once complete and final event details are submitted. Rush handling is available as an add-on when the schedule is tight.',
      },
    },
  ],
};

function finalizeMeta(page) {
  const path = normalizePath(page.path);
  const breadcrumbs = Array.isArray(page.breadcrumbs) ? page.breadcrumbs : null;
  const schemas = [];

  if (Array.isArray(page.structuredData)) {
    schemas.push(...page.structuredData);
  } else if (page.structuredData) {
    schemas.push(page.structuredData);
  }

  if (breadcrumbs && breadcrumbs.length > 1) {
    schemas.push(buildBreadcrumbSchema(breadcrumbs));
  }

  return {
    type: 'website',
    image: DEFAULT_OG_IMAGE,
    keywords: DEFAULT_KEYWORDS,
    robots: DEFAULT_ROBOTS,
    changefreq: 'monthly',
    priority: 0.6,
    ...page,
    path,
    canonical: page.canonical || toAbsoluteUrl(path),
    structuredData: schemas,
  };
}

const marketingPages = [
  finalizeMeta({
    path: '/',
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    changefreq: 'weekly',
    priority: 1,
    structuredData: [ORGANIZATION_SCHEMA, websiteSchema],
  }),
  finalizeMeta({
    path: '/invitations',
    title: 'Digital Invitation Designs Philippines | Wedding, Birthday, Christening, Corporate',
    description:
      'Browse digital invitation designs in the Philippines for weddings, birthdays, christenings, corporate events, and anniversaries. Personalized, mobile-friendly, RSVP-ready, and starting at P800.',
    changefreq: 'weekly',
    priority: 0.9,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Invitations', path: '/invitations' },
    ],
  }),
  finalizeMeta({
    path: '/digital-invitation-faq',
    title: 'Digital Invitation FAQ | Pricing, RSVP, and Personalized Design',
    description:
      'Read common questions about digital invitations in the Philippines, including P800 pricing, sample designs, personalized design, RSVP flow, and how online invitation pages work.',
    priority: 0.75,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Digital Invitation FAQ', path: '/digital-invitation-faq' },
    ],
    structuredData: digitalInvitationFaqSchema,
  }),
  finalizeMeta({
    path: '/book',
    title: 'Book Your Digital Invitation | E-Invitation Philippines',
    description:
      'Request a sample-based or personalized digital invitation for weddings, birthdays, christenings, anniversaries, and corporate events. Share your details and get started online.',
    changefreq: 'weekly',
    priority: 0.9,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Book', path: '/book' },
    ],
  }),
  finalizeMeta({
    path: '/about',
    title: 'About E-Invitation | Digital Invitation Design in the Philippines',
    description:
      'Learn how E-Invitation by Logica Technology creates mobile-friendly digital invitations, handles revisions, and delivers RSVP-ready pages for special events.',
    priority: 0.7,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'About', path: '/about' },
    ],
  }),
  finalizeMeta({
    path: '/contact',
    title: 'Contact E-Invitation | Digital Invitation Inquiries',
    description:
      'Contact E-Invitation by Logica Technology for digital invitation inquiries, custom design requests, pricing questions, and project support.',
    priority: 0.7,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Contact', path: '/contact' },
    ],
  }),
  finalizeMeta({
    path: '/pricing',
    title: 'E-Invitation Pricing | Digital Invitation Package Starts at P800',
    description:
      'See the standard P800 digital invitation package and optional add-ons, including RSVP-ready pages for weddings, birthdays, christenings, anniversaries, and corporate events.',
    priority: 0.8,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Pricing', path: '/pricing' },
    ],
  }),
  finalizeMeta({
    path: '/privacy',
    title: 'Privacy Policy | E-Invitation',
    description:
      'Read the privacy policy for E-Invitation by Logica Technology, including how event inquiry, booking, and RSVP information is handled.',
    changefreq: 'yearly',
    priority: 0.4,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Privacy Policy', path: '/privacy' },
    ],
  }),
  finalizeMeta({
    path: '/terms',
    title: 'Terms and Conditions | E-Invitation',
    description:
      'Review the terms and conditions for E-Invitation services, including booking expectations, revisions, delivery, and support.',
    changefreq: 'yearly',
    priority: 0.4,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Terms and Conditions', path: '/terms' },
    ],
  }),
];

const categoryLandingPages = [
  {
    path: '/wedding',
    breadcrumb: 'Wedding Invitations',
    title: 'Wedding Digital Invitations | RSVP-Ready Designs in the Philippines',
    description:
      'Explore wedding digital invitations for ceremonies, receptions, and save-the-dates. Browse elegant RSVP-ready designs built for mobile sharing in the Philippines.',
  },
  {
    path: '/birthday',
    breadcrumb: 'Birthday Invitations',
    title: 'Birthday Digital Invitations | Online Invitation Designs for Parties',
    description:
      'Browse birthday digital invitations for kids parties, themed celebrations, and milestone events. Compare online invitation designs that are easy to share.',
  },
  {
    path: '/christening',
    breadcrumb: 'Christening Invitations',
    title: 'Christening Digital Invitations | Baptism Invitation Designs',
    description:
      'Choose christening digital invitations for baptisms and family celebrations. Browse elegant mobile-friendly invitation designs with RSVP-ready delivery.',
  },
  {
    path: '/corporate',
    breadcrumb: 'Corporate Invitations',
    title: 'Corporate Digital Invitations | Business Event Invitation Designs',
    description:
      'Explore corporate digital invitations for seminars, launches, networking events, and company celebrations. Browse professional invitation designs ready for online sharing.',
  },
  {
    path: '/anniversary',
    breadcrumb: 'Anniversary Invitations',
    title: 'Anniversary Digital Invitations | Elegant Online Invitation Designs',
    description:
      'Browse anniversary digital invitations for silver, golden, and milestone celebrations. Explore elegant online invitation designs built for modern guest sharing.',
  },
].map((page) =>
  finalizeMeta({
    path: page.path,
    title: page.title,
    description: page.description,
    priority: 0.85,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Invitations', path: '/invitations' },
      { name: page.breadcrumb, path: page.path },
    ],
  })
);

const designPages = [
  {
    path: '/wedding/designs/classic-elegance',
    category: 'Wedding',
    title: 'Classic Elegance',
    description: 'Timeless and sophisticated wedding invitation with floral accents.',
  },
  {
    path: '/wedding/designs/modern-romance',
    category: 'Wedding',
    title: 'Modern Romance',
    description: 'Contemporary design with minimalist elegance.',
  },
  {
    path: '/wedding/designs/rustic-garden',
    category: 'Wedding',
    title: 'Rustic Garden',
    description: 'Nature-inspired wedding invitation design with botanical elements.',
  },
  {
    path: '/birthday/designs/kids-party-fun',
    category: 'Birthday',
    title: 'Kids Party Fun',
    description: "Colorful and playful digital invitation design for children's parties.",
  },
  {
    path: '/birthday/designs/elegant-milestone',
    category: 'Birthday',
    title: 'Elegant Milestone',
    description: 'Sophisticated design for milestone birthday celebrations.',
  },
  {
    path: '/birthday/designs/fiesta-theme',
    category: 'Birthday',
    title: 'Fiesta Theme',
    description: 'Vibrant Filipino fiesta-inspired invitation for birthdays and celebrations.',
  },
  {
    path: '/christening/designs/angelic-white',
    category: 'Christening',
    title: 'Angelic White',
    description: 'Pure and heavenly invitation design for baptisms and christenings.',
  },
  {
    path: '/christening/designs/blessed-journey',
    category: 'Christening',
    title: 'Blessed Journey',
    description: 'Beautiful religious-themed digital invitation for a special christening day.',
  },
  {
    path: '/corporate/designs/professional-blue',
    category: 'Corporate',
    title: 'Professional Blue',
    description: 'Clean and professional invitation design for business events and launches.',
  },
  {
    path: '/corporate/designs/executive-gold',
    category: 'Corporate',
    title: 'Executive Gold',
    description: 'Premium corporate invitation design for high-profile company events.',
  },
  {
    path: '/anniversary/designs/silver-anniversary',
    category: 'Anniversary',
    title: 'Silver Anniversary',
    description: 'Elegant silver-themed digital invitation design for 25th anniversaries.',
  },
  {
    path: '/anniversary/designs/golden-years',
    category: 'Anniversary',
    title: 'Golden Years',
    description: 'Luxurious gold-themed invitation design for anniversary celebrations.',
  },
].map((page) =>
  finalizeMeta({
    path: page.path,
    title: `${page.title} ${page.category} Invitation Design | E-Invitation`,
    description: `${page.description} Mobile-friendly, RSVP-ready, and personalized by E-Invitation Philippines.`,
    priority: 0.75,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Invitations', path: '/invitations' },
      { name: `${page.category} Invitations`, path: `/${page.category.toLowerCase()}` },
      { name: page.title, path: page.path },
    ],
  })
);

const socialSharePages = invitationMeta.map((page) =>
  finalizeMeta({
    ...page,
    robots: NOINDEX_ROBOTS,
    sitemap: false,
    priority: 0.2,
  })
);

const utilityPages = [
  finalizeMeta({
    path: '/review',
    title: 'Leave a Review | E-Invitation',
    description: 'Customer review form for completed E-Invitation projects.',
    robots: NOINDEX_ROBOTS,
    sitemap: false,
  }),
  finalizeMeta({
    path: '/seat-checker',
    title: 'Seat Checker | E-Invitation',
    description: 'Seat checker and guest access utility for invitation recipients.',
    robots: NOINDEX_ROBOTS,
    sitemap: false,
  }),
  finalizeMeta({
    path: '/e-photo-album/meri',
    title: 'Meri Photo Album | E-Invitation',
    description: 'Private wedding photo album upload page for Merizhelle Kate and Marvic.',
    robots: NOINDEX_ROBOTS,
    sitemap: false,
  }),
  finalizeMeta({
    path: '/admin/invitations',
    title: 'Admin Invitations Dashboard | E-Invitation',
    description: 'Administrative invitation dashboard.',
    robots: NOINDEX_ROBOTS,
    sitemap: false,
  }),
  finalizeMeta({
    path: '/debut/jenine-charisse/printable',
    title: 'Jenine Charisse Printable Invitation | E-Invitation',
    description: 'Printable invitation view for Jenine Charisse.',
    robots: NOINDEX_ROBOTS,
    sitemap: false,
  }),
  finalizeMeta({
    path: '/invitations/custom/sofia-alexandra-debut/printable',
    title: 'Sofia Alexandra Printable Invitation | E-Invitation',
    description: 'Printable invitation view for Sofia Alexandra.',
    robots: NOINDEX_ROBOTS,
    sitemap: false,
  }),
];

export const STATIC_META_PAGES = [
  ...marketingPages,
  ...categoryLandingPages,
  ...designPages,
  ...socialSharePages,
  ...utilityPages,
];

const STATIC_META_BY_PATH = new Map(STATIC_META_PAGES.map((page) => [page.path, page]));
const DESIGN_BASE_PATHS = new Set(designPages.map((page) => page.path));

function buildNoindexMeta(path, title, description, canonical = toAbsoluteUrl(path)) {
  return finalizeMeta({
    path,
    title,
    description,
    robots: NOINDEX_ROBOTS,
    canonical,
    sitemap: false,
    structuredData: [],
  });
}

export function resolveRouteMeta(pathname = '/') {
  const path = normalizePath(pathname);
  const exactMatch = STATIC_META_BY_PATH.get(path);
  if (exactMatch) return exactMatch;

  for (const designBasePath of DESIGN_BASE_PATHS) {
    if (path.startsWith(`${designBasePath}/`)) {
      const baseMeta = STATIC_META_BY_PATH.get(designBasePath);
      return buildNoindexMeta(
        path,
        `${baseMeta.title} | Preview Link`,
        `${baseMeta.description} Personalized preview links are excluded from search indexing.`,
        baseMeta.canonical
      );
    }
  }

  if (path.includes('/templates/')) {
    return buildNoindexMeta(
      path,
      'Legacy Design Preview | E-Invitation',
      'Legacy preview links are redirected to current design URLs and excluded from search indexing.'
    );
  }

  if (path.startsWith('/admin')) {
    return buildNoindexMeta(path, 'Admin Dashboard | E-Invitation', 'Administrative routes are excluded from search indexing.');
  }

  if (path.startsWith('/seat-checker')) {
    return buildNoindexMeta(path, 'Seat Checker | E-Invitation', 'Seat checker access links are excluded from search indexing.');
  }

  if (path.startsWith('/e-photo-album')) {
    return buildNoindexMeta(path, 'Private Photo Album | E-Invitation', 'Photo album upload and gallery links are excluded from search indexing.');
  }

  if (path.startsWith('/invitations/custom/')) {
    return buildNoindexMeta(path, 'Private Invitation | E-Invitation', 'Custom invitation share links are excluded from search indexing.');
  }

  if (path.endsWith('/printable') || path.startsWith('/printables/')) {
    return buildNoindexMeta(path, 'Printable Invitation | E-Invitation', 'Printable invitation views are excluded from search indexing.');
  }

  if (/^\/wedding\/[^/]+\/[^/]+$/.test(path)) {
    return buildNoindexMeta(path, 'Wedding Invitation Preview | E-Invitation', 'Personalized invitation preview links are excluded from search indexing.');
  }

  return buildNoindexMeta(path, 'Page Not Found | E-Invitation', 'This page is not available for search indexing.');
}

export function getSitemapEntries(lastmod = new Date().toISOString().slice(0, 10)) {
  return STATIC_META_PAGES.filter((page) => page.sitemap !== false && page.robots === DEFAULT_ROBOTS).map((page) => ({
    loc: page.canonical,
    lastmod,
    changefreq: page.changefreq,
    priority: page.priority.toFixed(1),
  }));
}

export function getStaticHtmlPages() {
  return STATIC_META_PAGES;
}

export function buildRobotsTxt() {
  return [
    'User-agent: *',
    'Allow: /',
    '',
    'Disallow: /dist/',
    'Disallow: /node_modules/',
    '',
    `Sitemap: ${SITE_URL}/sitemap.xml`,
    '',
  ].join('\n');
}

export function buildSitemapXml(lastmod = new Date().toISOString().slice(0, 10)) {
  const urls = getSitemapEntries(lastmod)
    .map(
      (entry) => `  <url>\n    <loc>${entry.loc}</loc>\n    <lastmod>${entry.lastmod}</lastmod>\n    <changefreq>${entry.changefreq}</changefreq>\n    <priority>${entry.priority}</priority>\n  </url>`
    )
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}
